import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import {
  CallableType,
  DateFormat,
  DefaultCollateralIdentifierType,
  DefaultMarket,
  EntityIdAndType,
  EntityType,
  EntityTypeEnum,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  UserPreference,
  WorkflowActions
} from 'api'
import { isOpenFin, NotificationPermissionEnum } from 'utils'
import { useNotificationPermission } from 'view/hooks'
import { CollapsePanel, Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { CounterpartyGroupsPanel } from './CounterpartyGroupsPanel'
import { DialogField, DialogRow, NumericSpinnerField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { HorizontalLayout, LabelPosition } from 'view/styles/constants'
import {
  AmountFormatSelectboxField,
  BooleanSelectboxField,
  DateFormatSelectboxField,
  DecisionMakerSelectboxField,
  DirectionFormatSelectboxField,
  InitiatedGridSelectBoxField,
  OTWSelectboxField,
  RFQSelectboxField,
  RFQTypeSelectboxField
} from '../entity'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { canViewInitiatedNegotiationsSelector, isBuySideTraderSelector, isSellSideTraderSelector } from 'selectors/permissionSelectors'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { Locale, Translation } from 'locale'
import { HaircutTypeSelectboxField } from '../entity/haircutOptions/HaircutTypeSelectbox'
import { CallableTypeSelectboxField } from '../entity/CallableTypeSelectbox'
import { DefaultCollateralIdentifierTypeSelectboxField } from '../entity/DefaultCollateralIdentifierTypeSelectbox'
import { DefaultMarketSelectboxField } from '../entity/DefaultMarketSelectbox'
import { HaircutSignSelectboxField } from '../entity/HaircutSignSelecbox'
import { BasketAllocationTimeSelectboxField } from '../entity/BaskedAllocationTimeSelectbox'
import { CopyPasteFormatSelectboxField } from '../entity/CopyPasteFormatSelectbox'
import { BrowserNotificationsSelectboxField } from '../entity/BrowserNotificationsSelectbox'

export type UserPreferenceDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  canViewInitiatedNegotiations: boolean
  userPreferenceEntity: ReferenceDataEntityType<UserPreference> | null
  availableEvents: ReferenceDataManagementEvent[]
  isBuySideTrader: boolean
  isSellSideTrader: boolean
  isAdmin: boolean
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: UserPreference; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}
const workflowIdSelector = (_state: {}, ownProps: UserPreferenceDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: UserPreferenceDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  userPreferenceEntity: createWorkflowReferenceDataEntitySelector(EntityType.USER_PREFERENCE, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  canViewInitiatedNegotiations: canViewInitiatedNegotiationsSelector,
  isBuySideTrader: isBuySideTraderSelector,
  isSellSideTrader: isSellSideTraderSelector,
  isAdmin: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)`
  max-height: calc(100vh - 300px);
  min-height: 450px;
  overflow-y: auto;
  width: 920px;
  display: flex;
  flex-wrap: wrap;

  & > * {
    margin-bottom: 1px;
  }
`

const UserPrefCollapsePanel = styled(CollapsePanel)`
  width: calc(50% - 20px);
  padding: 0 10px;
`

const CollapseContentWrapper = styled.div`
  padding: 20px;
  max-width: 410px;

  & label {
    flex: 0 0 58%;
  }

  & > *:not(:last-child) {
    margin-bottom: 19px;
  }
`

export const UserPreferenceDialogComponent: React.FunctionComponent<UserPreferenceDialogProps> = props => {
  const {
    canViewInitiatedNegotiations,
    workflowId,
    userPreferenceEntity,
    availableEvents,
    isBuySideTrader,
    isSellSideTrader,
    isAdmin,
    onUpdate,
    onSubmitWorkflow,
    onCancelWorkflow,
    onClose
  } = props

  const { notificationPermission, requestPermission } = useNotificationPermission()
  const [notificationPermissionDraft, setNotificationPermissionDraft] = useState<NotificationPermission>(notificationPermission)

  if (!userPreferenceEntity) {
    return null
  }

  const userPreference = userPreferenceEntity.data

  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const callableDaysReadOnly = readOnly || userPreference.defaultCallableType === CallableType.NONE
  const validationIssues = userPreferenceEntity.validationIssues ?? []

  const updateHandler = <K extends keyof UserPreference>(key: K) => {
    return (value: UserPreference[K]) => {
      onUpdate({
        entityId: userPreference.id,
        entityType: EntityTypeEnum.USER_PREFERENCE,
        updatedEntity: {
          ...userPreference,
          [key]: value
        },
        workflowId
      })
    }
  }

  const callableDefaultUpdateHandler = (value: CallableType) => {
    onUpdate({
      entityId: userPreference.id,
      entityType: EntityTypeEnum.USER_PREFERENCE,
      updatedEntity: {
        ...userPreference,
        defaultCallableType: value,
        callablePeriodDaysDefault: null
      },
      workflowId
    })
  }

  const defaultMarketUpdateHandler = (value: DefaultMarket) => {
    if (value === DefaultMarket.CUSTOM) {
      return
    }

    const updatedUserPreference: UserPreference = {
      ...userPreference,
      defaultCollateralIdentifierType:
        value === DefaultMarket.EU ? DefaultCollateralIdentifierType.ISIN : DefaultCollateralIdentifierType.CUSIP,
      dateFormat: value === DefaultMarket.EU ? DateFormat.EU : DateFormat.US
    }

    onUpdate({
      entityId: userPreference.id,
      entityType: EntityTypeEnum.USER_PREFERENCE,
      updatedEntity: updatedUserPreference,
      workflowId
    })
  }

  const getDefaultMarket: () => DefaultMarket = () => {
    if (
      userPreference.defaultCollateralIdentifierType === DefaultCollateralIdentifierType.ISIN &&
      userPreference.dateFormat === DateFormat.EU
    ) {
      return DefaultMarket.EU
    }

    if (
      userPreference.defaultCollateralIdentifierType === DefaultCollateralIdentifierType.CUSIP &&
      userPreference.dateFormat === DateFormat.US
    ) {
      return DefaultMarket.US
    }

    return DefaultMarket.CUSTOM
  }

  const onSave = () => {
    if (
      notificationPermissionDraft &&
      notificationPermission !== NotificationPermissionEnum.GRANTED &&
      notificationPermissionDraft === NotificationPermissionEnum.GRANTED
    ) {
      requestPermission()
    }
    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const canSave = () => {
    return !readOnly && validationIssues.length === 0
  }

  return (
    <ModalWindow dataTestId={'UserPreferenceDialog-ModalWindow-UserPreference'} title={Locale.translate(Translation.TC_USER_PREFERENCE)}>
      <Content>
        <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_GENERAL)} defaultCollapsed={false}>
          <CollapseContentWrapper>
            <DirectionFormatSelectboxField
              dataTestId={'UserPreferenceDialog-DirectionFormatSelectboxField-DirectionFormat'}
              fieldName="directionFormat"
              label={Locale.translate(Translation.SC_DIRECTION_FORMAT)}
              labelPosition={LabelPosition.Left}
              onUpdate={updateHandler('directionFormat')}
              value={userPreference.directionFormat}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
            <DefaultMarketSelectboxField
              dataTestId={'UserPreferenceDialog-DefaultMarketSelectboxField-DefaultMarket'}
              fieldName="defaultMarket"
              label={Locale.translate(Translation.SC_DEFAULT_MARKET)}
              labelPosition={LabelPosition.Left}
              onUpdate={defaultMarketUpdateHandler}
              value={getDefaultMarket()}
              readOnly={readOnly}
            />
            <DateFormatSelectboxField
              dataTestId={'UserPreferenceDialog-DateFormatSelectboxField-DateFormatDefault'}
              fieldName="dateFormat"
              label={Locale.translate(Translation.SC_DATE_FORMAT_DEFAULT)}
              labelPosition={LabelPosition.Left}
              onUpdate={updateHandler('dateFormat')}
              value={userPreference.dateFormat}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
            <DefaultCollateralIdentifierTypeSelectboxField
              dataTestId={'UserPreferenceDialog-DefaultCollateralIdentifierTypeSelectboxField-DefaultCollateralIdentifierType'}
              fieldName="defaultCollateralIdentifierType"
              label={Locale.translate(Translation.SC_PRIMARY_IDENTIFIER)}
              labelPosition={LabelPosition.Left}
              onUpdate={updateHandler('defaultCollateralIdentifierType')}
              value={userPreference.defaultCollateralIdentifierType}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
            <AmountFormatSelectboxField
              dataTestId={'UserPreferenceDialog-DateFormatSelectboxField-AmountFormatDefault'}
              fieldName="amountFormat"
              label={Locale.translate(Translation.SC_AMOUNT_FORMAT_DEFAULT)}
              labelPosition={LabelPosition.Left}
              onUpdate={updateHandler('amountFormat')}
              value={userPreference.amountFormat}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
            {canViewInitiatedNegotiations && (
              <InitiatedGridSelectBoxField
                dataTestId={'UserPreferenceDialog-InitiatedGridSelectBoxField-InitiatedGridView'}
                fieldName="counterPartyGridView"
                label={Locale.translate(Translation.SC_INITIATED_GRID_VIEW)}
                labelPosition={LabelPosition.Left}
                onUpdate={updateHandler('counterPartyGridView')}
                value={userPreference.counterPartyGridView}
                readOnly={readOnly}
                validationIssues={validationIssues}
              />
            )}
            <HaircutSignSelectboxField
              dataTestId={'UserPreferenceDialog-HaircutSignSelectboxField-HaircutSignDefault'}
              fieldName="haircutSign"
              label={Locale.translate(Translation.SC_HAIRCUT_SIGN)}
              labelPosition={LabelPosition.Left}
              isSellSideTrader={!isAdmin && !isBuySideTrader}
              onUpdate={updateHandler('haircutSign')}
              value={userPreference.haircutSign}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
            {isBuySideTrader && (
              <BasketAllocationTimeSelectboxField
                dataTestId={'UserPreferenceDialog-DateFormatSelectboxField-RFQDurationDefault'}
                fieldName="rfqDurationMinsDefault"
                label={Locale.translate(Translation.SC_BASKET_ALLOCATION_TIME)}
                labelPosition={LabelPosition.Left}
                onUpdate={updateHandler('basketAllocationDurationMinsDefault')}
                value={userPreference.basketAllocationDurationMinsDefault}
                readOnly={readOnly}
                validationIssues={validationIssues}
              />
            )}
            <CopyPasteFormatSelectboxField
              dataTestId="UserPreferenceDialog-CopyPasteFormatSelectboxField-CopyPasteFormat"
              fieldName="copyPasteFormat"
              label={Locale.translate(Translation.TC_COPY_PASTE_FORMAT)}
              labelPosition={LabelPosition.Left}
              onUpdate={updateHandler('copyPasteFormat')}
              value={userPreference.copyPasteFormat}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </CollapseContentWrapper>
        </UserPrefCollapsePanel>
        {!isAdmin &&
          (isOpenFin() ? (
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_NOTIFICATIONS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                <BooleanSelectboxField
                  dataTestId={'UserPreferenceDialog-BooleanSelectboxField-Noric'}
                  fieldName="notificationsEnabled"
                  label={Locale.translate(Translation.SC_SHOW_TOAST_NOTIFICATIONS)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={isOpenFin() ? updateHandler('notificationsEnabled') : requestPermission}
                  value={isOpenFin() ? userPreference.notificationsEnabled : notificationPermission === NotificationPermissionEnum.GRANTED}
                  readOnly={readOnly}
                  validationIssues={validationIssues}
                />
                <BooleanSelectboxField
                  dataTestId={'UserPreferenceDialog-BooleanSelectboxField-PlayAudioOnNotification'}
                  fieldName="playAudibleAlertOnNotification"
                  label={Locale.translate(Translation.SC_PLAY_ALERT_ON_NOTIFICATION)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={updateHandler('playAudibleAlertOnNotification')}
                  value={userPreference.playAudibleAlertOnNotification}
                  readOnly={readOnly}
                  validationIssues={validationIssues}
                />
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
          ) : (
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_BROWSER_NOTIFICATIONS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                <BrowserNotificationsSelectboxField
                  label={Locale.translate(Translation.SC_SHOW_BROWSER_NOTIFICATIONS)}
                  labelPosition={LabelPosition.Left}
                  readOnly={notificationPermission !== 'default'}
                  value={notificationPermissionDraft}
                  onUpdate={value => value && setNotificationPermissionDraft(value)}
                />
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
          ))}
        {isBuySideTrader && (
          <>
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_PREPARE)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                <BooleanSelectboxField
                  dataTestId={'UserPreferenceDialog-BooleanSelectboxField-ShowCompetitionDefault'}
                  fieldName="showCompetitionDefault"
                  label={Locale.translate(Translation.SC_SHOW_COMPETITION_DEFAULT)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={updateHandler('showCompetitionDefault')}
                  value={userPreference.showCompetitionDefault}
                  readOnly={readOnly}
                  validationIssues={validationIssues}
                />
                <RFQSelectboxField
                  dataTestId={'UserPreferenceDialog-DateFormatSelectboxField-RFQDurationDefault'}
                  fieldName="rfqDurationMinsDefault"
                  label={Locale.translate(Translation.SC_RFQ_DURATION_DEFAULT)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={updateHandler('rfqDurationMinsDefault')}
                  value={userPreference.rfqDurationMinsDefault}
                  readOnly={readOnly}
                  validationIssues={validationIssues}
                />
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_COUNTERPARTY_GROUPS)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                <CounterpartyGroupsPanel
                  userPreference={userPreference}
                  readOnly={readOnly}
                  onUpdate={updateHandler('counterpartyGroups')}
                />
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
          </>
        )}
        {!isAdmin && (
          <>
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_NEGOTIATION)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                {isSellSideTrader && (
                  <>
                    <OTWSelectboxField
                      dataTestId={'UserPreferenceDialog-DateFormatSelectboxField-OTWDurationDefault'}
                      fieldName="otwDurationMinsDefault"
                      label={Locale.translate(Translation.SC_OTW_DURATION_DEFAULT)}
                      labelPosition={LabelPosition.Left}
                      onUpdate={updateHandler('otwDurationMinsDefault')}
                      value={userPreference.otwDurationMinsDefault}
                      readOnly={readOnly}
                      validationIssues={validationIssues}
                    />
                    <HaircutTypeSelectboxField
                      fieldName="defaultHaircutType"
                      label={Locale.translate(Translation.SC_DEFAULT_HAIRCUT_TYPE)}
                      labelPosition={LabelPosition.Left}
                      onUpdate={updateHandler('defaultHaircutType')}
                      value={userPreference.defaultHaircutType}
                      canClear={false}
                      readOnly={readOnly}
                      validationIssues={validationIssues}
                    />
                  </>
                )}
                {isBuySideTrader && (
                  <>
                    <CallableTypeSelectboxField
                      dataTestId={'UserPreferenceDialog-CallableTypeSelectboxField-CallableDefault'}
                      fieldName="callableDefault"
                      label={Locale.translate(Translation.SC_CALLABLE_DEFAULT)}
                      labelPosition={LabelPosition.Left}
                      value={userPreference.defaultCallableType}
                      onUpdate={callableDefaultUpdateHandler}
                      readOnly={readOnly}
                      validationIssues={validationIssues}
                    />
                    <NumericSpinnerField
                      dataTestId={'UserPreferenceDialog-NumericSpinnerField-CallableDaysDefault'}
                      fieldName="callablePeriodDaysDefault"
                      label={Locale.translate(Translation.SC_CALLABLE_DAYS_DEFAULT)}
                      labelPosition={LabelPosition.Left}
                      min={1}
                      disabled={!userPreference.defaultCallableType}
                      onUpdate={updateHandler('callablePeriodDaysDefault')}
                      value={userPreference.callablePeriodDaysDefault === 0 ? null : userPreference.callablePeriodDaysDefault}
                      readOnly={callableDaysReadOnly}
                      validationIssues={validationIssues}
                    />
                    <RFQTypeSelectboxField
                      dataTestId={'UserPreferenceDialog-RFQTypeSelectboxField-RFQTypeDefault'}
                      fieldName="defaultRfqType"
                      label={Locale.translate(Translation.SC_RFQ_TYPE_DEFAULT)}
                      labelPosition={LabelPosition.Left}
                      onUpdate={updateHandler('defaultRfqType')}
                      value={userPreference.defaultRfqType}
                      readOnly={readOnly}
                      validationIssues={validationIssues}
                    />
                  </>
                )}
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
            <UserPrefCollapsePanel title={Locale.translate(Translation.TC_USER_PREFERENCE_REGULATORY)} defaultCollapsed={false}>
              <CollapseContentWrapper>
                <DecisionMakerSelectboxField
                  dataTestId={'UserPreferenceDialog-DecisionMakerSelectboxField-DefaultIDM'}
                  fieldName="defaultInvestmentDecisionMaker"
                  label={Locale.translate(Translation.SC_DEFAULT_INVESTMENT_DECISION_MAKER)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={updateHandler('defaultInvestmentDecisionMaker')}
                  value={userPreference.defaultInvestmentDecisionMaker}
                  readOnly={readOnly}
                  canClear={true}
                  validationIssues={validationIssues}
                />
                <DecisionMakerSelectboxField
                  dataTestId={'UserPreferenceDialog-DecisionMakerSelectboxField-DefaultEDM'}
                  fieldName="defaultExecutionDecisionMaker"
                  label={Locale.translate(Translation.SC_DEFAULT_EXECUTION_DECISION_MAKER)}
                  labelPosition={LabelPosition.Left}
                  onUpdate={updateHandler('defaultExecutionDecisionMaker')}
                  value={userPreference.defaultExecutionDecisionMaker}
                  readOnly={readOnly}
                  canClear={true}
                  validationIssues={validationIssues}
                />
              </CollapseContentWrapper>
            </UserPrefCollapsePanel>
          </>
        )}
      </Content>
      <ModalFooter>
        <DialogRow horizontalLayout={HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton dataTestId={'UserPreferenceDialog-TextButton-ButtonCancel'} type={TextButtonType.Bad} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'UserPreferenceDialog-TextButton-ButtonSave'}
              type={TextButtonType.Good}
              disabled={!canSave()}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const UserPreferenceDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(UserPreferenceDialogComponent)
